import Badge from '@atlaskit/badge';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import ChangesIcon from '@atlaskit/icon/core/changes';
import CopyIcon from '@atlaskit/icon/core/copy';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EditIcon from '@atlaskit/icon/core/edit';
import FilterIcon from '@atlaskit/icon/core/filter';
import PinIcon from '@atlaskit/icon/core/pin';
import SortAscendingIcon from '@atlaskit/icon/core/sort-ascending';
import SortDescendingIcon from '@atlaskit/icon/core/sort-descending';
import TableCellClearIcon from '@atlaskit/icon/core/table-cell-clear';
import ArrowsDiagonalUpRightDownLeftIcon from '@atlaskit/icon-lab/core/arrows-diagonal-up-right-down-left';
import { type TriggerProps } from '@atlaskit/popup';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { actionBarState } from '@databases/action-bar/action-bar-state';
import { actionBarPortalState } from '@databases/action-bar/selections-effect';
import {
  getDatabaseContainerType,
  getSelectionsAnalyticsAttributes,
  SendDatabaseSortEventSources,
  sendSortEvent,
  useAnalytics
} from '@databases/analytics';
import ContextualMenu, { type MenuItem } from '@databases/contextual-menu/ContextualMenu';
import {
  contextualMenuState,
  type MenuData
} from '@databases/contextual-menu/contextual-menu-state';
import { MENU_WIDTH } from '@databases/contextual-menu/menu-constants';
import { useContextualMenu } from '@databases/contextual-menu/useContextualMenu';
import {
  hasAiSummaryHeaderRefresh,
  useAiSummaryRerunMenuItems,
  useIsAiSummaryFieldEnabled
} from '@databases/database-ai-fields';
import {
  InlineRovoPromptTriggerSource,
  openInlineRovoPrompt
} from '@databases/database-ai-first-creation-state/inline-rovo-prompt-state';
import { AskRovoIcon } from '@databases/database-ai-first-creation-ui/icons/ask-rovo-icon';
import { useDialog } from '@databases/dialog';
import FieldSettingsMenuContent from '@databases/field-settings-menus/FieldSettingsMenuContent';
import { useChangeFieldType } from '@databases/field-type/FieldTypeEdit';
import { useFieldTypeEntries } from '@databases/field-type/use-field-type-entries';
import { getFieldTypes } from '@databases/fields';
import {
  DatabaseTraceAction,
  DatabaseTraceSource,
  traceDatabaseAction
} from '@databases/frontend-metrics/perf-tracing';
import {
  ActiveHorizontalDragHandlerIcon,
  HideIcon,
  HorizontalDragHandlerIcon,
  InsertColumnAfterIcon,
  InsertColumnBeforeIcon,
  SortIcon
} from '@databases/icons';
import { FieldTypeIcon } from '@databases/icons/FieldTypeIcon';
import { messages } from '@databases/intl/messages';
import { SHORTCUTS } from '@databases/keyboard-shortcuts';
import usePanel from '@databases/panel/usePanel';
import usePanelState from '@databases/panel/usePanelState';
import { useQuickCalcMenuItem } from '@databases/quick-calc/useQuickCalcMenuItem';
import { type HoverProps, useComponent, useKeyboardShortcut } from '@databases/react';
import { getTextDelta } from '@databases/react/useTextDelta';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import {
  doClearEntireFields,
  doDeleteFieldsWithIds,
  doDuplicateEntireFields,
  doInsertFields,
  doMoveFields,
  doSelectNewFields
} from '@databases/redux/contextual-menu/actions';
import { getService } from '@databases/service-container';
import { IGNORED_TABLE_ELEMENT_CLASS } from '@databases/table-resizer';
import { DatabaseFieldType } from '@databases/types/database';
import {
  type DatabaseFilter,
  type DatabaseFilterUsageType,
  type DatabaseViewSettings
} from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import { fieldTypeSupportsAskRovo } from '@databases/utils/database/field-types';
import {
  fieldHasCalculations,
  getDatabaseFieldRequired,
  getDatabaseFields,
  getExistingFieldNames
} from '@databases/utils/database/utils';
import {
  type ViewChangeType,
  ViewChangeTypes
} from '@databases/utils/database/view/view';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { afterNextLayout } from '@databases/utils/lib/raf';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { ViewPanelSettingType } from '@databases/views/types';
import useView from '@databases/views/useView';
import { useViewUiState } from '@databases/views/useViewUiState';
import React, {
  type ForwardedRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';
import { useSnapshot } from 'valtio';

import { getExperimentValue } from '@canvas/client-feature-flags';
import { manuallyFireExposureEvent } from '@canvas/client-feature-flags/experiments';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import FieldMenuHeader, { type FieldMenuHeaderHandle } from './FieldMenuHeader.tsx';
import { createFieldSortSubmenuItems } from './field-sort-submenu.tsx';
import { ColSelectButtonContainer, DragHandlerWrapper } from './styles.tsx';
import { Position } from './type.ts';

const unfreezeIconContainerStyles = xcss({
  alignItems: 'center',
  display: 'inline-flex',
  height: '16px',
  justifyContent: 'center',
  position: 'relative',
  width: '16px'
});

const unfreezeIconSlashStyles = xcss({
  borderBlockStartColor: 'currentColor',
  borderBlockStartStyle: 'solid',
  borderBlockStartWidth: token('border.width', '1px'),
  position: 'absolute',
  transform: 'rotate(45deg)',
  width: '18px'
});

const fieldTypeSubmenuHeadingStyles = xcss({
  paddingBlockStart: 'space.100',
  paddingInlineEnd: 'space.150',
  paddingInlineStart: 'space.200'
});

const UnfreezeIcon = () => (
  <Box as="span" aria-hidden="true" xcss={unfreezeIconContainerStyles}>
    <PinIcon label="" />
    <Box as="span" xcss={unfreezeIconSlashStyles} />
  </Box>
);

enum SELECT_FIELD_MENU_ACTIONS {
  ASK_ROVO = 'ask_rovo',
  EDIT_FIELD = 'edit_field',
  CHANGE_FIELD_TYPE = 'change_field_type',
  PIN_THROUGH_COLUMN = 'pin_through_column',
  UNPIN_COLUMN = 'unpin_column',
  ADD_FIELD_LEFT = 'add_field_left',
  ADD_FIELD_RIGHT = 'add_field_right',
  DUPLICATE_FIELDS = 'duplicate_fields',
  CLEAR_CELLS = 'clear_cells',
  DELETE_FIELDS = 'delete_fields',
  MOVE_FIELDS_LEFT = 'move_fields_left',
  MOVE_FIELDS_RIGHT = 'move_fields_right',
  FILTER_FIELD = 'filter_field',
  SORT_FIELD_INCREASING = 'sort_field_increasing',
  SORT_FIELD_DECREASING = 'sort_field_decreasing',
  HIDE_FIELDS = 'hide_fields'
}

export type PinnedLeadingFieldMenuConfig = {
  fieldIndex: number;
  pinnedLeadingFieldCount: number;
  maxPinnedLeadingFieldCount: number;
  onPinThroughThisColumn: () => void;
  onUnpinAllColumns: () => void;
  onFieldsInserted: ({
    insertBefore,
    selectedFieldIds
  }: {
    insertBefore: boolean;
    selectedFieldIds: ReadonlyArray<string>;
  }) => void;
  onFieldsRemoved: (fieldIds: ReadonlyArray<string>) => void;
  getViewChangesForFieldsHidden: (
    fieldIds: ReadonlyArray<string>
  ) => ReadonlyArray<ViewChangeType>;
};

type SelectFieldMenuProps = {
  contentId: string;
  fieldId: string;
  currentViewId: string;
  viewSettings?: DatabaseViewSettings;
  hoverProps?: HoverProps;
  canEditFields: boolean;
  handleSelectColumn: (e: React.MouseEvent | React.KeyboardEvent) => void;
  contextMenuAnchorPosition?: { x: number; y: number } | null;
  onOpenFromDragHandle?: () => void;
  onEditAiSummaryField?: () => void;
  hasUserEditedAiSummaryCells: () => boolean;
  onAiSummaryRerunEntireColumn?: () => void;
  onAiSummaryRerunEmptyEntries?: () => void;
  isAiSummaryRerunDisabled?: boolean;
  pinnedLeadingMenu?: PinnedLeadingFieldMenuConfig;
};

export type SelectFieldMenuHandle = {
  openAtPosition: (position: { x: number; y: number }) => Promise<void>;
};

const EMPTY_SELECTED_FIELD_IDS: ReadonlyArray<string> = [];

const getIsFieldSelected = (fieldId: string) =>
  getService('SelectionsReader').isFieldSelected(fieldId);

const hasFieldBefore = (databaseFields: ReadonlyArray<{ id: string }>, fieldId: string) =>
  databaseFields.findIndex((field) => field.id === fieldId) > 0;

const hasFieldAfter = (
  databaseFields: ReadonlyArray<{ id: string }>,
  fieldId: string
) => {
  const fieldIndex = databaseFields.findIndex((field) => field.id === fieldId);

  return fieldIndex >= 0 && fieldIndex < databaseFields.length - 1;
};

const SelectFieldMenu = (
  {
    contentId,
    fieldId,
    currentViewId,
    viewSettings,
    hoverProps,
    handleSelectColumn,
    contextMenuAnchorPosition: externalContextMenuAnchorPosition,
    onOpenFromDragHandle,
    onEditAiSummaryField,
    hasUserEditedAiSummaryCells,
    onAiSummaryRerunEntireColumn,
    onAiSummaryRerunEmptyEntries,
    isAiSummaryRerunDisabled,
    canEditFields,
    pinnedLeadingMenu
  }: SelectFieldMenuProps,
  ref: ForwardedRef<SelectFieldMenuHandle>
) => {
  const selectionsReader = getService('SelectionsReader');
  const isSelectionsPerformanceEnabled = getExperimentValue(
    'databasesSelectionsPerformance',
    'isEnabled',
    { fireExperimentExposure: false }
  );
  const actionBar = useSnapshot(actionBarState.state);
  const actionBarPortal = useSnapshot(actionBarPortalState);
  const isActionBarEnabled = isSelectionsPerformanceEnabled
    ? actionBarPortal.isVisible
    : actionBar.isActionBarEnabled;
  const isFieldContextualMenusEnabled = getExperimentValue(
    'databasesContextualMenusM2',
    'enabled',
    { fireExperimentExposure: false }
  );
  const isContextualMenusM2Enabled = isFieldContextualMenusEnabled;
  const [isMenuSelectionActive, setIsMenuSelectionActive] = useState(false);
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const { database, databaseFields } = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId
    });

    return {
      database,
      databaseFields: getDatabaseFields(database)
    };
  });

  // Guard the heavier reads behind isSelected so unselected columns skip them.
  const isSelected = selectionsReader.isFieldSelected(fieldId) || isMenuSelectionActive;
  const selectedFieldIds = !isSelected
    ? EMPTY_SELECTED_FIELD_IDS
    : isMenuSelectionActive
      ? [fieldId]
      : selectionsReader.getSelectedFieldIds();
  const selectedFieldCount = selectedFieldIds.length || 1;
  const isMultiFieldSelection = selectedFieldCount > 1;
  const isDeletionDisabled = isSelected && selectionsReader.isDeleteColumnsDisabled();
  const isSingleFieldMenu = !isFieldContextualMenusEnabled || !isMultiFieldSelection;
  const menuFieldCount = isFieldContextualMenusEnabled ? selectedFieldCount : 1;
  const hasFieldBeforeSelection =
    isSelected &&
    isSingleFieldMenu &&
    (isMenuSelectionActive
      ? hasFieldBefore(databaseFields, fieldId)
      : selectionsReader.hasFieldBeforeSelection());
  const hasFieldAfterSelection =
    isSelected &&
    (isMenuSelectionActive
      ? hasFieldAfter(databaseFields, fieldId)
      : selectionsReader.hasFieldAfterSelection());
  const canInsertFields = canEditFields && isSelected && isSingleFieldMenu;
  const canDuplicateFields = canEditFields && isSelected;
  const canDeleteFields = canEditFields && isSelected && !isDeletionDisabled;
  const canClearCells = canEditFields && isSelected;
  const canMoveRight = canEditFields && isSingleFieldMenu && hasFieldAfterSelection;
  const canMoveLeft = canEditFields && hasFieldBeforeSelection;
  const selectionIntent = isSelected ? selectionsReader.getSelectionIntent() : null;
  const isHoverDeleteFieldsButton =
    selectionIntent?.type === 'delete' && selectionIntent?.target === 'fields';

  const { formatMessage } = useIntl();
  const selectionsActions = getService('SelectionsActions');
  const { setViewChange, view } = useView();
  const client = useAnalytics();
  const { hidePanel, showPanel } = usePanel();
  const { getData } = usePanelState();
  const { fieldId: panelFieldId } = getData('FieldSettingsPanel') || {};

  const field = getDatabaseFieldRequired(database, fieldId);
  const aiSummaryRerunMenuItems = useAiSummaryRerunMenuItems({
    confirmationKey: `${contentId}:${fieldId}`,
    hasUserEditedCells: hasUserEditedAiSummaryCells,
    onRerunEntireColumn: onAiSummaryRerunEntireColumn,
    onRerunEmptyEntries: onAiSummaryRerunEmptyEntries,
    isDisabled: isAiSummaryRerunDisabled
  });
  const hasAiFieldHeaderRefresh = hasAiSummaryHeaderRefresh(field.type);
  const existingFieldNames = getExistingFieldNames(database, field);
  const isPinnedLeadingColumnsEnabled = getExperimentValue(
    'databasesPinnedLeadingColumns',
    'enabled',
    { fireExperimentExposure: false }
  );
  const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');

  const menuData: MenuData = {
    type: 'field',
    fieldId
  };
  const { isMenuOpen, openMenu, closeMenu, toggleOpenMenu } = useContextualMenu({
    type: 'field',
    fieldId
  });
  const [headerContextMenuAnchorPosition, setHeaderContextMenuAnchorPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const contextMenuAnchorPosition =
    externalContextMenuAnchorPosition ?? headerContextMenuAnchorPosition;

  const fieldMenuHeaderRef = useRef<FieldMenuHeaderHandle>(null);
  const prevIsSelected = useRef(getIsFieldSelected(fieldId));
  const isOpeningMenu = useRef(false);
  const selectFieldForMenu = useCallback(
    ({ shouldClearSelectionIntent = true } = {}) => {
      if (!getIsFieldSelected(fieldId)) {
        selectionsActions.selectFields([fieldId]);
        setIsMenuSelectionActive(true);
      } else {
        setIsMenuSelectionActive(false);
      }

      if (shouldClearSelectionIntent) {
        selectionsActions.clearSelectionIntent();
      }
    },
    [fieldId, selectionsActions]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.shiftKey || e.metaKey || e.ctrlKey) {
        if (isMenuOpen) {
          closeMenu();
        }

        contextualMenuState.closeMenu();
        isOpeningMenu.current = false;
        prevIsSelected.current = true;
        setHeaderContextMenuAnchorPosition(null);
        setIsMenuSelectionActive(false);
        handleSelectColumn(e);
        return;
      }

      if (isMenuOpen) {
        isOpeningMenu.current = false;
        setHeaderContextMenuAnchorPosition(null);
        setIsMenuSelectionActive(false);
        closeMenu();
        return;
      }

      setHeaderContextMenuAnchorPosition(null);
      onOpenFromDragHandle?.();
      isOpeningMenu.current = true;

      if (!getIsFieldSelected(fieldId)) {
        prevIsSelected.current = true;
      } else {
        prevIsSelected.current = false;
      }
      selectFieldForMenu();

      afterNextLayout(async () => {
        try {
          if (isContextualMenusM2Enabled) {
            manuallyFireExposureEvent('databasesContextualMenusM2');
          }
          await client.sendFieldContextualMenuOpened({
            contentId: contentId,
            databaseContainer: getDatabaseContainerType(),
            ...getSelectionsAnalyticsAttributes(contentId)
          });
          selectFieldForMenu({ shouldClearSelectionIntent: false });
          openMenu();
        } finally {
          isOpeningMenu.current = false;
        }
      });
    },
    [
      client,
      closeMenu,
      contentId,
      fieldId,
      handleSelectColumn,
      isContextualMenusM2Enabled,
      isMenuOpen,
      openMenu,
      onOpenFromDragHandle,
      selectFieldForMenu
    ]
  );

  useImperativeHandle(
    ref,
    () => ({
      async openAtPosition(position) {
        setHeaderContextMenuAnchorPosition(position);

        if (!getIsFieldSelected(fieldId)) {
          prevIsSelected.current = true;
        } else {
          prevIsSelected.current = false;
        }
        selectFieldForMenu();

        afterNextLayout(async () => {
          if (!isMenuOpen) {
            await client.sendFieldContextualMenuOpened({
              contentId,
              databaseContainer: getDatabaseContainerType(),
              ...getSelectionsAnalyticsAttributes(contentId)
            });
          }

          selectFieldForMenu({ shouldClearSelectionIntent: false });
          openMenu();
          isOpeningMenu.current = false;
        });
      }
    }),
    [client, contentId, fieldId, isMenuOpen, openMenu, selectFieldForMenu]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (
        getIsFieldSelected(fieldId) &&
        !prevIsSelected.current &&
        !isMenuOpen &&
        !isOpeningMenu.current
      ) {
        // remove selection if the user clicks on the select button again
        handleSelectColumn(e);
      }
    },
    [fieldId, handleSelectColumn, isMenuOpen]
  );

  const handleRightClick = (e: React.MouseEvent) => e.preventDefault();
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') {
        return;
      }

      e.preventDefault();

      setHeaderContextMenuAnchorPosition(null);
      onOpenFromDragHandle?.();

      if (!isMenuOpen) {
        if (isContextualMenusM2Enabled) {
          manuallyFireExposureEvent('databasesContextualMenusM2');
        }
        await client.sendFieldContextualMenuOpened({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          ...getSelectionsAnalyticsAttributes(contentId)
        });
      }

      toggleOpenMenu();

      selectFieldForMenu();
    },
    [
      client,
      contentId,
      isContextualMenusM2Enabled,
      isMenuOpen,
      onOpenFromDragHandle,
      selectFieldForMenu,
      toggleOpenMenu
    ]
  );

  const handleSaveFieldName = useCallback(
    (name: string) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_FIELD_NAME,
          fieldId,
          name,
          delta: getTextDelta(field.name, name, field.name.length)
        })
      );
    },
    [contentId, dispatch, field.name, fieldId]
  );

  const handleSaveFieldDescription = useCallback(
    (description: string) => {
      const currentDescription = field.description ?? '';
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_FIELD_DESCRIPTION,
          fieldId,
          description,
          delta: getTextDelta(currentDescription, description, currentDescription.length)
        })
      );
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_SHOW_FIELD_DESCRIPTION,
          fieldId,
          showDescription: description.length > 0
        })
      );
    },
    [contentId, dispatch, field.description, fieldId]
  );

  const handleMenuClose = useCallback(
    (event?: Event | React.MouseEvent | React.KeyboardEvent | null) => {
      const isEscapeEvent = event != null && 'key' in event && event.key === 'Escape';
      if (isContextualMenusM2Enabled && !isEscapeEvent) {
        fieldMenuHeaderRef.current?.save();
      }
      isOpeningMenu.current = false;
      setHeaderContextMenuAnchorPosition(null);
      setIsMenuSelectionActive(false);
    },
    [isContextualMenusM2Enabled]
  );

  const Trigger = useComponent(
    ({ ref, ...triggerProps }: TriggerProps) => (
      <ColSelectButtonContainer className={IGNORED_TABLE_ELEMENT_CLASS} {...hoverProps}>
        {contextMenuAnchorPosition && (
          <span
            ref={ref}
            style={{
              position: 'fixed',
              // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
              left: contextMenuAnchorPosition.x,
              // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
              top: contextMenuAnchorPosition.y,
              width: 1,
              height: 1,
              pointerEvents: 'none'
            }}
          />
        )}
        <DragHandlerWrapper
          ref={contextMenuAnchorPosition ? undefined : ref}
          type="button"
          className={TABLE_CLASSNAMES.SELECT_BUTTON}
          isActive={isMenuOpen && getIsFieldSelected(fieldId)}
          isDeletionActive={isHoverDeleteFieldsButton}
          data-interactive
          data-testid="field-select-button"
          {...triggerProps}
          aria-label={formatMessage(messages['table-layout.select-column-label'])}
          aria-expanded={isMenuOpen}
          aria-haspopup="menu"
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onClick={handleClick}
          onContextMenu={handleRightClick}
          onKeyDown={handleKeyDown}
          style={{
            backgroundColor:
              isMenuOpen && getIsFieldSelected(fieldId)
                ? token('color.background.accent.blue.subtler.pressed')
                : token('color.background.accent.gray.subtlest.hovered')
          }}
        >
          <div className={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}>
            <ActiveHorizontalDragHandlerIcon
              label={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}
            />
          </div>
          <div className={TABLE_CLASSNAMES.DRAG_HANDLER_ICON}>
            <HorizontalDragHandlerIcon label={TABLE_CLASSNAMES.DRAG_HANDLER_ICON} />
          </div>
        </DragHandlerWrapper>
      </ColSelectButtonContainer>
    ),
    [
      hoverProps,
      contextMenuAnchorPosition,
      isMenuOpen,
      fieldId,
      isHoverDeleteFieldsButton,
      formatMessage,
      handleMouseDown,
      handleMouseUp,
      handleClick,
      handleKeyDown
    ]
  );

  const handleInsertFields = useCallback(
    async (position: Position) => {
      const selectedFieldIds = selectionsReader.getFieldIdsFromSelection();
      const newFieldLabel = formatMessage(
        messages['field-settings.field-name-inline-edit-default-text'],
        {
          defaultFieldName: formatMessage(getFieldTypes()[DatabaseFieldType.TEXT].label)
        }
      );
      await dispatch(
        doSelectNewFields(contentId, () =>
          traceDatabaseAction(
            {
              action:
                position === Position.BEFORE
                  ? DatabaseTraceAction.FieldInsertBefore
                  : DatabaseTraceAction.FieldInsertAfter,
              source: DatabaseTraceSource.FieldMenu
            },
            () => {
              dispatch(
                doInsertFields(
                  contentId,
                  currentViewId,
                  position === Position.BEFORE,
                  newFieldLabel
                )
              );
              pinnedLeadingMenu?.onFieldsInserted({
                insertBefore: position === Position.BEFORE,
                selectedFieldIds
              });
            }
          )
        )
      );
      await client.sendInsertRowEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType()
      });
    },
    [
      client,
      contentId,
      currentViewId,
      dispatch,
      formatMessage,
      pinnedLeadingMenu,
      selectionsReader
    ]
  );

  const handleDuplicateFields = useCallback(() => {
    const selectedFieldIds = isPinnedLeadingColumnsEnabled
      ? selectionsReader.getFieldIdsFromSelection()
      : [];
    traceDatabaseAction(
      {
        action: DatabaseTraceAction.FieldDuplicate,
        source: DatabaseTraceSource.FieldMenu
      },
      () => {
        dispatch(
          doSelectNewFields(contentId, () => {
            dispatch(doDuplicateEntireFields(contentId, view.id));
            if (isPinnedLeadingColumnsEnabled) {
              pinnedLeadingMenu?.onFieldsInserted({
                insertBefore: false,
                selectedFieldIds
              });
            }
          })
        );
      }
    );
  }, [
    contentId,
    dispatch,
    isPinnedLeadingColumnsEnabled,
    pinnedLeadingMenu,
    selectionsReader,
    view.id
  ]);

  const handleClearFields = useCallback(() => {
    traceDatabaseAction(
      { action: DatabaseTraceAction.FieldClear, source: DatabaseTraceSource.FieldMenu },
      () => {
        dispatch(doClearEntireFields(contentId));
      }
    );
  }, [contentId, dispatch]);

  const { showDialog } = useDialog();

  const onLeaveDeleteFieldButton = useCallback(() => {
    selectionsActions.clearSelectionIntent();
  }, [selectionsActions]);

  const handleDeleteFields = useCallback(() => {
    const fieldNames: string[] = [];
    const database = selectDatabaseByContentIdRequired(store.getState(), {
      contentId
    });
    const selectedEntireFieldIds = getService('SelectionsReader').getSelectedFieldIds();
    selectedEntireFieldIds.forEach((fieldId) => {
      if (database.fieldsById[fieldId]) {
        fieldNames.push(database.fieldsById[fieldId].name);
      }
    });
    showDialog('ConfirmDialog', {
      heading: formatMessage(messages['field-settings.delete-field']),
      body: (
        <Stack space="space.150">
          <Box>
            {formatMessage(
              messages[
                'field-settings.delete-multiple-fields-confirmation-message.fields'
              ],
              {
                fieldsNumber: fieldNames.length,
                fields: <Text weight="bold">{fieldNames.join(', ')}</Text>
              }
            )}
          </Box>
          <Box>
            {formatMessage(
              messages[
                'field-settings.delete-multiple-fields-confirmation-message.additional'
              ]
            )}
          </Box>
        </Stack>
      ),
      yesLabel: formatMessage(messages['delete-buttons.delete-field-button']),
      noLabel: formatMessage(messages['delete-buttons.delete-field-cancel-button']),
      appearance: 'danger',
      onClose: (result: boolean) => {
        onLeaveDeleteFieldButton();
        if (result) {
          if (getService('SelectionsReader').isFieldInSelection(panelFieldId)) {
            hidePanel();
          }

          if (fieldHasCalculations(fieldId, viewSettings?.calculations)) {
            client.sendRemoveCalculationEvent({
              databaseContainer: getDatabaseContainerType(),
              fieldId,
              fieldType: database.fieldsById[fieldId].type,
              source: 'selectFieldMenu',
              contentId: database.contentId
            });
          }

          traceDatabaseAction(
            {
              action: DatabaseTraceAction.FieldDelete,
              source: DatabaseTraceSource.FieldMenu
            },
            () => {
              dispatch(
                doDeleteFieldsWithIds(
                  contentId,
                  selectedEntireFieldIds,
                  isPinnedLeadingColumnsEnabled && pinnedLeadingMenu
                    ? () => pinnedLeadingMenu.onFieldsRemoved(selectedEntireFieldIds)
                    : undefined
                )
              );

              if (isSelectionsPerformanceEnabled) {
                selectionsActions.deselectAll();
              }
            }
          );

          selectedEntireFieldIds.forEach((id) => {
            const deletedField = database.fieldsById[id];
            if (deletedField) {
              void client.sendDeleteFieldEvent({
                contentId,
                databaseContainer: getDatabaseContainerType(),
                fieldIdDeleted: deletedField.id,
                fieldName: deletedField.name,
                fieldType: deletedField.type
              });
            }
          });
        }
      }
    });
  }, [
    store,
    contentId,
    showDialog,
    formatMessage,
    onLeaveDeleteFieldButton,
    panelFieldId,
    fieldId,
    viewSettings?.calculations,
    dispatch,
    client,
    hidePanel,
    isPinnedLeadingColumnsEnabled,
    isSelectionsPerformanceEnabled,
    pinnedLeadingMenu,
    selectionsActions
  ]);

  const handleMoveFields = useCallback(
    (position: Position) => {
      traceDatabaseAction(
        {
          action:
            position === Position.BEFORE
              ? DatabaseTraceAction.FieldMoveLeft
              : DatabaseTraceAction.FieldMoveRight,
          source: DatabaseTraceSource.FieldMenu
        },
        () => {
          dispatch(doMoveFields(contentId, viewSettings, position === Position.BEFORE));
        }
      );

      if (isSelectionsPerformanceEnabled) {
        afterNextLayout(() => selectionsActions.selectFields([fieldId]));
      }
    },
    [
      contentId,
      dispatch,
      fieldId,
      isSelectionsPerformanceEnabled,
      selectionsActions,
      viewSettings
    ]
  );

  const handleSortChange = useCallback(
    (sort: DatabaseViewSettings['sorts'][number]) => {
      const matchingSort = view.settings.sorts.find((s) => s.fieldId === sort.fieldId);
      if (matchingSort) {
        setViewChange({
          type: ViewChangeTypes.VIEW_SORT_UPDATE_VALUE,
          fieldId: sort.fieldId,
          ascending: sort.ascending,
          customOrder: sort.customOrder
        });
      } else {
        setViewChange({
          type: ViewChangeTypes.VIEW_SORT_APPEND,
          sort
        });
      }
      void sendSortEvent(
        client,
        sort,
        SendDatabaseSortEventSources.FIELD_MENU,
        field ? field.type : undefined,
        contentId
      );
    },
    [client, contentId, field, setViewChange, view.settings.sorts]
  );

  const filters = view.settings.filter as
    | {
        type: DatabaseFilterUsageType;
        value: DatabaseFilter[];
      }
    | undefined;

  const fieldFilters = useMemo(
    () =>
      filters?.value.filter((filter) => 'fieldId' in filter && filter.fieldId === fieldId)
        .length || 0,
    [filters, fieldId]
  );
  const isSortAppliedToField = useMemo(
    () => view.settings.sorts.some((sort) => sort.fieldId === fieldId),
    [fieldId, view.settings.sorts]
  );

  const { setActivePanelSetting } = useViewUiState();
  const isViewContextualMenusEnabled = getExperimentValue(
    'databasesViewContextualMenus',
    'enabled',
    { fireExperimentExposure: false }
  );
  const isAiFieldsSummaryEnabled = useIsAiSummaryFieldEnabled();
  const shouldShowFieldMenuHeader =
    isFieldContextualMenusEnabled ||
    (field.type === DatabaseFieldType.AI_SUMMARY && isAiFieldsSummaryEnabled);
  const handleFilterField = useCallback(() => {
    manuallyFireExposureEvent('databasesViewContextualMenus');

    if (isViewContextualMenusEnabled) {
      contextualMenuState.showMenu({
        type: 'view-filter',
        viewId: view.id,
        fieldIdForFilterToCreate: fieldId
      });
    } else {
      setActivePanelSetting(ViewPanelSettingType.FILTER);
      showPanel('ViewSettingsPanel', {
        canEditViews: canEditFields,
        fieldIdForFilterToCreate: fieldId
      });
    }
  }, [
    fieldId,
    isViewContextualMenusEnabled,
    setActivePanelSetting,
    showPanel,
    canEditFields,
    view.id
  ]);

  const handleHideFields = useCallback(() => {
    const database = selectDatabaseByContentIdRequired(store.getState(), {
      contentId
    });
    const fields = getDatabaseFields(database);
    const selectedFieldIdsToHide = isMenuSelectionActive
      ? [fieldId]
      : selectionsReader.getSelectedFieldIds();
    const fieldIdsToHide =
      isFieldContextualMenusEnabled && selectedFieldIdsToHide.length > 0
        ? selectedFieldIdsToHide
        : [fieldId];
    const viewChanges: ViewChangeType[] = fieldIdsToHide.map((fieldIdToHide) => ({
      type: ViewChangeTypes.VIEW_FIELDS,
      fieldKey: fieldIdToHide,
      fieldValue: false,
      fields
    }));
    const pinnedLeadingViewChanges =
      pinnedLeadingMenu?.getViewChangesForFieldsHidden(fieldIdsToHide) ?? [];
    setViewChange(
      pinnedLeadingViewChanges.length > 0
        ? [...viewChanges, ...pinnedLeadingViewChanges]
        : viewChanges
    );
    if (isSelectionsPerformanceEnabled) {
      selectionsActions.deselectAll();
    }
    const field = getDatabaseFieldRequired(database, fieldId);
    void client.sendHideColumnsEvent({
      source: 'databaseView',
      contentId: contentId,
      databaseContainer: getDatabaseContainerType(),
      hidden: true,
      fieldType: field.type
    });
  }, [
    client,
    contentId,
    fieldId,
    isFieldContextualMenusEnabled,
    isMenuSelectionActive,
    isSelectionsPerformanceEnabled,
    pinnedLeadingMenu,
    selectionsActions,
    selectionsReader,
    setViewChange,
    store
  ]);

  const addFieldLeftMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.ADD_FIELD_LEFT,
      content: formatMessage(messages['field-actions.add-left']),
      onClick: () => handleInsertFields(Position.BEFORE),
      shortcut: SHORTCUTS.ADD_FIELD_LEFT,
      elemBefore: <InsertColumnBeforeIcon label="" />
    }),
    [formatMessage, handleInsertFields]
  );

  const addFieldRightMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.ADD_FIELD_RIGHT,
      content: formatMessage(messages['field-actions.add-right']),
      onClick: () => handleInsertFields(Position.AFTER),
      shortcut: SHORTCUTS.ADD_FIELD_RIGHT,
      elemBefore: <InsertColumnAfterIcon label="" />
    }),
    [formatMessage, handleInsertFields]
  );

  const duplicateFieldsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.DUPLICATE_FIELDS,
      content:
        isFieldContextualMenusEnabled && selectedFieldCount === 1
          ? formatMessage(messages['field-actions.duplicate-single'])
          : formatMessage(messages['field-actions.duplicate'], {
              fieldCount: menuFieldCount
            }),
      onClick: handleDuplicateFields,
      shortcut: SHORTCUTS.DUPLICATE,
      elemBefore: <CopyIcon label="" />
    }),
    [
      formatMessage,
      handleDuplicateFields,
      isFieldContextualMenusEnabled,
      menuFieldCount,
      selectedFieldCount
    ]
  );

  const clearCellsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.CLEAR_CELLS,
      content: formatMessage(messages['value-actions.clear-cells'], { valuesCount: 2 }),
      onClick: handleClearFields,
      shortcut: SHORTCUTS.CLEAR,
      elemBefore: isFieldContextualMenusEnabled ? (
        <TableCellClearIcon label="" />
      ) : (
        <CrossCircleIcon label="" />
      )
    }),
    [formatMessage, handleClearFields, isFieldContextualMenusEnabled]
  );

  const onHoverDeleteFieldButton = useCallback(() => {
    selectionsActions.setSelectionIntent({ type: 'delete', target: 'fields' });
  }, [selectionsActions]);

  const deleteFieldsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.DELETE_FIELDS,
      content:
        isFieldContextualMenusEnabled && selectedFieldCount === 1
          ? formatMessage(messages['field-actions.delete-single'])
          : formatMessage(messages['field-actions.delete'], {
              fieldCount: menuFieldCount
            }),
      onClick: handleDeleteFields,
      onMouseEnter: onHoverDeleteFieldButton,
      onMouseLeave: onLeaveDeleteFieldButton,
      shortcut: SHORTCUTS.DELETE,
      elemBefore: (
        <DeleteIcon
          label=""
          color={
            isHoverDeleteFieldsButton ? token('color.icon.danger', '#C9372C') : undefined
          }
        />
      )
    }),
    [
      formatMessage,
      handleDeleteFields,
      isHoverDeleteFieldsButton,
      isFieldContextualMenusEnabled,
      menuFieldCount,
      onHoverDeleteFieldButton,
      onLeaveDeleteFieldButton,
      selectedFieldCount
    ]
  );

  const moveFieldsLeftMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.MOVE_FIELDS_LEFT,
      content: formatMessage(messages['field-actions.move-left'], {
        fieldCount: 1
      }),
      onClick: () => handleMoveFields(Position.BEFORE),
      shortcut: SHORTCUTS.MOVE_FIELDS_LEFT,
      elemBefore: <ArrowLeftIcon label="" />
    }),
    [formatMessage, handleMoveFields]
  );

  const moveFieldsRightMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.MOVE_FIELDS_RIGHT,
      content: formatMessage(messages['field-actions.move-right'], {
        fieldCount: 1
      }),
      onClick: () => handleMoveFields(Position.AFTER),
      shortcut: SHORTCUTS.MOVE_FIELDS_RIGHT,
      elemBefore: <ArrowRightIcon label="" />
    }),
    [formatMessage, handleMoveFields]
  );

  const filterFieldItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.FILTER_FIELD,
      content: formatMessage(
        messages[
          isFieldContextualMenusEnabled
            ? 'field-actions.filter-short'
            : 'field-actions.filter'
        ]
      ),
      onClick: handleFilterField,
      elemAfter:
        fieldFilters > 0 ? (
          <Badge
            style={{
              backgroundColor: token(
                'color.background.accent.blue.subtlest.hovered',
                '#CCE0FF'
              )
            }}
            testId="filters-badge"
          >
            {fieldFilters}
          </Badge>
        ) : undefined,
      elemBefore: <FilterIcon label="" />
    }),
    [fieldFilters, formatMessage, handleFilterField, isFieldContextualMenusEnabled]
  );

  const sortFieldIncreasingItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.SORT_FIELD_INCREASING,
      content: formatMessage(messages['field-actions.sort-increasing']),
      onClick: () =>
        handleSortChange({
          fieldId,
          ascending: true
        }),
      elemBefore: <SortDescendingIcon label="" />
    }),
    [fieldId, formatMessage, handleSortChange]
  );

  const sortFieldDecreasingItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.SORT_FIELD_DECREASING,
      content: formatMessage(messages['field-actions.sort-decreasing']),
      onClick: () =>
        handleSortChange({
          fieldId,
          ascending: false
        }),
      elemBefore: <SortAscendingIcon label="" />
    }),
    [fieldId, formatMessage, handleSortChange]
  );

  const sortFieldSubmenuItems: MenuItem[] = useMemo(
    () =>
      createFieldSortSubmenuItems({
        field,
        fieldId,
        formatMessage,
        onSortChange: handleSortChange
      }),
    [field, fieldId, formatMessage, handleSortChange]
  );

  const sortFieldItem: MenuItem = useMemo(
    () => ({
      key: 'sort-field',
      content: formatMessage(messages['field-settings-picker.sort-picker-button-text']),
      submenuItems: sortFieldSubmenuItems,
      isActive: isSortAppliedToField,
      elemBefore: <SortIcon label="" />
    }),
    [formatMessage, isSortAppliedToField, sortFieldSubmenuItems]
  );

  const hideFieldsItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.HIDE_FIELDS,
      content: formatMessage(
        messages[
          isFieldContextualMenusEnabled && selectedFieldCount === 1
            ? 'field-actions.hide-single'
            : 'field-actions.hide-fields'
        ],
        {
          fieldCount: menuFieldCount
        }
      ),
      onClick: handleHideFields,
      value: { name: SELECT_FIELD_MENU_ACTIONS.HIDE_FIELDS },
      elemBefore: <HideIcon label="" />
    }),
    [
      formatMessage,
      handleHideFields,
      isFieldContextualMenusEnabled,
      menuFieldCount,
      selectedFieldCount
    ]
  );

  const insertFieldMenuItem: MenuItem = useMemo(
    () => ({
      key: 'insert-field',
      content: formatMessage(messages['table-layout.insert-column-tooltip']),
      submenuItems: [addFieldLeftMenuItem, addFieldRightMenuItem],
      elemBefore: <InsertColumnAfterIcon label="" />
    }),
    [formatMessage, addFieldLeftMenuItem, addFieldRightMenuItem]
  );

  const handlePinnedLeadingMenuAction = useCallback(
    ({
      action,
      nextFrozenColumnCount,
      onAction
    }: {
      action: 'pinned' | 'unpinned';
      nextFrozenColumnCount: number;
      onAction: () => void;
    }) => {
      if (!pinnedLeadingMenu) {
        return;
      }

      const previousFrozenColumnCount = pinnedLeadingMenu.pinnedLeadingFieldCount;
      const reliability = ReliabilityMetrics.getInstance();

      try {
        onAction();
        reliability.success(MetricName.DATABASE_UPDATE_FROZEN_COLUMNS, { action });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        reliability.failure(MetricName.DATABASE_UPDATE_FROZEN_COLUMNS, {
          action,
          errorMessage
        });
        throw error;
      }

      void client.sendFrozenColumnsUpdatedEvent({
        action,
        contentId,
        databaseContainer: getDatabaseContainerType(),
        source: 'fieldContextualMenu',
        previousFrozenColumnCount,
        nextFrozenColumnCount,
        targetFieldIndex: pinnedLeadingMenu.fieldIndex,
        maxFrozenColumnCount: pinnedLeadingMenu.maxPinnedLeadingFieldCount,
        ...getSelectionsAnalyticsAttributes(contentId)
      });
      selectionsActions.deselectAll();
    },
    [client, contentId, pinnedLeadingMenu, selectionsActions]
  );

  const pinThroughColumnMenuItem: MenuItem | null = useMemo(() => {
    if (!isPinnedLeadingColumnsEnabled || !pinnedLeadingMenu) {
      return null;
    }
    const wouldExceedMax =
      pinnedLeadingMenu.fieldIndex + 1 > pinnedLeadingMenu.maxPinnedLeadingFieldCount;
    const isFieldFrozen =
      pinnedLeadingMenu.fieldIndex < pinnedLeadingMenu.pinnedLeadingFieldCount;

    if (isFieldFrozen) {
      return null;
    }

    return {
      key: SELECT_FIELD_MENU_ACTIONS.PIN_THROUGH_COLUMN,
      content: formatMessage(messages['field-actions.pin-through-column']),
      closeOnClick: !wouldExceedMax,
      isDisabled: wouldExceedMax,
      tooltipDescription: wouldExceedMax
        ? formatMessage(messages['field-actions.pin-through-column-disabled-at-limit'])
        : undefined,
      tooltipPosition: 'right',
      onClick: () =>
        handlePinnedLeadingMenuAction({
          action: 'pinned',
          nextFrozenColumnCount: pinnedLeadingMenu.fieldIndex + 1,
          onAction: pinnedLeadingMenu.onPinThroughThisColumn
        }),
      elemBefore: <PinIcon label="" />
    };
  }, [
    formatMessage,
    handlePinnedLeadingMenuAction,
    isPinnedLeadingColumnsEnabled,
    pinnedLeadingMenu
  ]);

  const unpinAllColumnsMenuItem: MenuItem | null = useMemo(() => {
    if (!isPinnedLeadingColumnsEnabled || !pinnedLeadingMenu) {
      return null;
    }
    if (pinnedLeadingMenu.fieldIndex >= pinnedLeadingMenu.pinnedLeadingFieldCount) {
      return null;
    }
    return {
      key: SELECT_FIELD_MENU_ACTIONS.UNPIN_COLUMN,
      content: formatMessage(messages['field-actions.unpin-column']),
      closeOnClick: true,
      onClick: () =>
        handlePinnedLeadingMenuAction({
          action: 'unpinned',
          nextFrozenColumnCount: 0,
          onAction: pinnedLeadingMenu.onUnpinAllColumns
        }),
      elemBefore: <UnfreezeIcon />
    };
  }, [
    formatMessage,
    handlePinnedLeadingMenuAction,
    isPinnedLeadingColumnsEnabled,
    pinnedLeadingMenu
  ]);

  const RovoElemBefore = () => {
    useEffect(() => {
      getExperimentValue('databasesAIFC', 'enabled', {
        fireExperimentExposure: true
      });
    }, []);
    return <AskRovoIcon height={16} width={16} noPadding />;
  };

  const askRovoFieldMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.ASK_ROVO,
      name: 'ask-rovo',
      content: formatMessage(messages['value-actions.ask-rovo']),
      onClick: () => openInlineRovoPrompt(InlineRovoPromptTriggerSource.TableField),
      elemBefore: <RovoElemBefore />
    }),
    [formatMessage]
  );

  const editFieldMenuItem: MenuItem = useMemo(() => {
    const title = formatMessage(messages['table-layout.field-settings.label']);

    if (field.type === DatabaseFieldType.AI_SUMMARY) {
      return {
        key: SELECT_FIELD_MENU_ACTIONS.EDIT_FIELD,
        content: title,
        onClick: onEditAiSummaryField,
        elemBefore: <EditIcon label="" />
      };
    }

    return {
      key: SELECT_FIELD_MENU_ACTIONS.EDIT_FIELD,
      content: {
        type: 'drilldown-menu',
        title,
        width: MENU_WIDTH.DRILLDOWN,
        content: () => (
          <FieldSettingsMenuContent contentId={contentId} fieldId={fieldId} />
        )
      },
      elemBefore: <EditIcon label="" />
    };
  }, [contentId, field.type, fieldId, formatMessage, onEditAiSummaryField]);

  const { basic, confluence, jira } = useFieldTypeEntries(undefined, 'contextual-menu');
  const { field: draftField, changeFieldType } = useChangeFieldType({
    contentId,
    fieldId
  });
  const fieldTypeSubmenuItems: MenuItem[] = useMemo(() => {
    const submenuItems: MenuItem[] = [];
    const addSection = (key: string, label: string, fieldTypes: typeof basic) => {
      if (fieldTypes.length === 0) {
        return;
      }

      submenuItems.push({
        key: `${key}_heading`,
        content: (
          <Box xcss={fieldTypeSubmenuHeadingStyles}>
            <Text size="small" weight="semibold" color="color.text.subtle">
              {label}
            </Text>
          </Box>
        )
      });
      submenuItems.push(
        ...fieldTypes.map((fieldType) => {
          const Icon =
            fieldType.type === DatabaseFieldType.JIRA_ISSUE_DETAILS
              ? ArrowsDiagonalUpRightDownLeftIcon
              : fieldType.Icon;
          const isSelected = draftField.type === fieldType.type;
          const iconColor = isSelected ? token('color.icon.selected') : undefined;
          const content =
            fieldType.type === DatabaseFieldType.HYPERLINK
              ? formatMessage(messages['field-settings.change-field-type.hyperlink'])
              : fieldType.type === DatabaseFieldType.ATTACHMENT
                ? formatMessage(messages['field-settings.change-field-type.attachment'])
                : fieldType.label;
          return {
            key: `change_field_type_${fieldType.type}`,
            content,
            elemBefore: Icon ? (
              <Icon label="" color={iconColor} />
            ) : (
              <FieldTypeIcon
                type={fieldType.type}
                label={fieldType.labelDescriptor}
                noTooltip
                color={iconColor}
              />
            ),
            isActive: isSelected,
            onClick: () => changeFieldType(fieldType.type)
          };
        })
      );
    };

    addSection(
      'change_field_type_basic',
      formatMessage(messages['field-settings.change-field-type.basic-fields.non-final']),
      basic
    );
    addSection(
      'change_field_type_confluence',
      formatMessage(
        messages['field-settings.change-field-type.advanced-confluence-fields.non-final']
      ),
      confluence
    );
    addSection(
      'change_field_type_jira',
      formatMessage(
        messages['field-settings.change-field-type.advanced-jira-fields.non-final']
      ),
      jira
    );

    return submenuItems;
  }, [basic, changeFieldType, confluence, draftField.type, formatMessage, jira]);

  const changeFieldTypeMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.CHANGE_FIELD_TYPE,
      content: formatMessage(messages['field-settings.change-field-type']),
      elemBefore: <ChangesIcon label="" />,
      submenuItems: fieldTypeSubmenuItems,
      submenuWidth: MENU_WIDTH.SUBMENU,
      submenuMaxHeight: '328px',
      submenuScrollGradient: true
    }),
    [fieldTypeSubmenuItems, formatMessage]
  );

  const quickCalculationItem = useQuickCalcMenuItem({
    field,
    contentId,
    canEditFields
  });

  const menuItems = useMemo(() => {
    const firstGroup: MenuItem[] = [];
    const isSingleEditableFieldMenu =
      !isFieldContextualMenusEnabled || (canEditFields && !isMultiFieldSelection);
    const rerunGroup: MenuItem[] =
      isSingleEditableFieldMenu && isAiFieldsSummaryEnabled && hasAiFieldHeaderRefresh
        ? aiSummaryRerunMenuItems
        : [];
    const secondGroup: MenuItem[] = [];
    const thirdGroup: MenuItem[] = [];
    const fourthGroup: MenuItem[] = [];

    if (
      isDBAiFirstCreationEnabled &&
      fieldTypeSupportsAskRovo(field.type) &&
      getExperimentValue('databasesAIFC', 'enabled', {
        fireExperimentExposure: false
      })
    ) {
      firstGroup.push(askRovoFieldMenuItem);
    }
    const shouldShowEditField =
      field.type === DatabaseFieldType.AI_SUMMARY
        ? isAiFieldsSummaryEnabled && Boolean(onEditAiSummaryField)
        : isFieldContextualMenusEnabled;

    if (shouldShowEditField && isSingleEditableFieldMenu) {
      firstGroup.push(editFieldMenuItem);
      firstGroup.push(changeFieldTypeMenuItem);
    }

    if (isFieldContextualMenusEnabled && !canEditFields && !isMultiFieldSelection) {
      secondGroup.push(filterFieldItem);
      secondGroup.push(sortFieldItem);
    } else if (isFieldContextualMenusEnabled && isMultiFieldSelection) {
      secondGroup.push(hideFieldsItem);
      if (canDuplicateFields) {
        secondGroup.push(duplicateFieldsMenuItem);
      }
      if (canClearCells) {
        thirdGroup.push(clearCellsMenuItem);
      }
      if (canDeleteFields) {
        thirdGroup.push(deleteFieldsMenuItem);
      }
    } else if (isFieldContextualMenusEnabled) {
      secondGroup.push(filterFieldItem);
      secondGroup.push(sortFieldItem);
      secondGroup.push(hideFieldsItem);
      if (quickCalculationItem) {
        secondGroup.push(quickCalculationItem);
      }
      if (unpinAllColumnsMenuItem) {
        secondGroup.push(unpinAllColumnsMenuItem);
      }
      if (pinThroughColumnMenuItem) {
        secondGroup.push(pinThroughColumnMenuItem);
      }
      if (canInsertFields) {
        thirdGroup.push(insertFieldMenuItem);
      }
      if (canDuplicateFields) {
        thirdGroup.push(duplicateFieldsMenuItem);
      }
      if (canClearCells) {
        fourthGroup.push(clearCellsMenuItem);
      }
      if (canDeleteFields) {
        fourthGroup.push(deleteFieldsMenuItem);
      }
    } else {
      if (canInsertFields) {
        secondGroup.push(addFieldRightMenuItem);
        secondGroup.push(addFieldLeftMenuItem);
      }
      if (canDuplicateFields) {
        secondGroup.push(duplicateFieldsMenuItem);
      }
      if (canClearCells) {
        secondGroup.push(clearCellsMenuItem);
      }
      if (canDeleteFields) {
        secondGroup.push(deleteFieldsMenuItem);
      }
      if (canMoveLeft) {
        thirdGroup.push(moveFieldsLeftMenuItem);
      }
      if (canMoveRight) {
        thirdGroup.push(moveFieldsRightMenuItem);
      }
      if (unpinAllColumnsMenuItem) {
        fourthGroup.push(unpinAllColumnsMenuItem);
      }
      if (pinThroughColumnMenuItem) {
        fourthGroup.push(pinThroughColumnMenuItem);
      }
      if (quickCalculationItem) {
        fourthGroup.push(quickCalculationItem);
      }
      fourthGroup.push(filterFieldItem);
      fourthGroup.push(sortFieldIncreasingItem);
      fourthGroup.push(sortFieldDecreasingItem);
      fourthGroup.push(hideFieldsItem);
    }
    return [
      { items: firstGroup },
      { items: rerunGroup },
      { items: secondGroup },
      { items: thirdGroup },
      { items: fourthGroup }
    ];
  }, [
    canInsertFields,
    canDuplicateFields,
    canClearCells,
    canDeleteFields,
    canMoveLeft,
    canMoveRight,
    aiSummaryRerunMenuItems,
    askRovoFieldMenuItem,
    editFieldMenuItem,
    changeFieldTypeMenuItem,
    addFieldRightMenuItem,
    addFieldLeftMenuItem,
    duplicateFieldsMenuItem,
    clearCellsMenuItem,
    deleteFieldsMenuItem,
    moveFieldsLeftMenuItem,
    moveFieldsRightMenuItem,
    quickCalculationItem,
    filterFieldItem,
    sortFieldIncreasingItem,
    sortFieldDecreasingItem,
    sortFieldItem,
    hideFieldsItem,
    hasAiFieldHeaderRefresh,
    field.type,
    canEditFields,
    isAiFieldsSummaryEnabled,
    isFieldContextualMenusEnabled,
    isMultiFieldSelection,
    isDBAiFirstCreationEnabled,
    onEditAiSummaryField,
    pinThroughColumnMenuItem,
    unpinAllColumnsMenuItem,
    insertFieldMenuItem
  ]);

  useKeyboardShortcut(
    isMenuOpen && canInsertFields ? SHORTCUTS.ADD_FIELD_LEFT.keyboardShortCut : undefined,
    () => handleInsertFields(Position.BEFORE)
  );
  useKeyboardShortcut(
    isMenuOpen && canInsertFields
      ? SHORTCUTS.ADD_FIELD_RIGHT.keyboardShortCut
      : undefined,
    () => handleInsertFields(Position.AFTER)
  );
  useKeyboardShortcut(
    isMenuOpen && canDuplicateFields ? SHORTCUTS.DUPLICATE.keyboardShortCut : undefined,
    handleDuplicateFields
  );
  // Suppress CLEAR when the selections engine is active — it owns Backspace
  // for all modes to avoid duplicate dispatches and broken undo.
  useKeyboardShortcut(
    isMenuOpen && canClearCells && !isSelectionsPerformanceEnabled
      ? SHORTCUTS.CLEAR.keyboardShortCut
      : undefined,
    handleClearFields
  );
  useKeyboardShortcut(
    isMenuOpen && canDeleteFields ? SHORTCUTS.DELETE.keyboardShortCut : undefined,
    handleDeleteFields
  );
  useKeyboardShortcut(
    isMenuOpen && canMoveRight ? SHORTCUTS.MOVE_FIELDS_RIGHT.keyboardShortCut : undefined,
    () => handleMoveFields(Position.AFTER)
  );
  useKeyboardShortcut(
    isMenuOpen && canMoveLeft ? SHORTCUTS.MOVE_FIELDS_LEFT.keyboardShortCut : undefined,
    () => () => handleMoveFields(Position.BEFORE)
  );

  return (
    <ContextualMenu
      menuData={menuData}
      items={menuItems}
      trigger={Trigger}
      header={
        shouldShowFieldMenuHeader && isSingleFieldMenu ? (
          <FieldMenuHeader
            ref={fieldMenuHeaderRef}
            name={field.name}
            description={field.description}
            existingFieldNames={existingFieldNames}
            onSaveName={handleSaveFieldName}
            onSaveDescription={handleSaveFieldDescription}
            onRequestClose={closeMenu}
          />
        ) : undefined
      }
      onClose={handleMenuClose}
      placement="bottom-start"
      isActionBarEnabled={isActionBarEnabled && !contextMenuAnchorPosition}
      width={MENU_WIDTH.PRIMARY}
    />
  );
};

export default React.memo(React.forwardRef(SelectFieldMenu));
